import React, {
  useState, createContext, useContext, useRef, useEffect, forwardRef
} from "react";
import type { ReactNode, ReactElement, KeyboardEvent } from "react";
import clsx from "clsx";
import { FaChevronDown } from "react-icons/fa";

export interface AccordionI18nContext {
  getHeaderLabel?: (index: number, label?: string) => string;
}

type AccordionContextType = {
  openIndex: number | number[];
  setOpenIndex: (index: number | number[]) => void;
  theme: "light" | "dark";
  registerHeader: (ref: HTMLDivElement | null, idx: number) => void;
  headerRefs: React.RefObject<(HTMLDivElement | null)[]>;
  multiOpen: boolean;
  disableTransitions: boolean;          // NEW
  unmountOnExit: boolean;               // NEW
};

export const AccordionContext = createContext<AccordionContextType | undefined>(undefined);

export const AccordionMessagesContext = createContext<AccordionI18nContext>({
  getHeaderLabel: (i, label) => label ?? `Seção ${i + 1}`,
});

type AccordionProps = {
  children: ReactNode;
  defaultOpenIndex?: number | number[] | null;
  openIndex?: number | number[];
  onChange?: (index: number | number[]) => void;
  theme?: "light" | "dark";
  className?: string;
  "data-testid"?: string;
  dataTestId?: string;                 // NEW (alternativa sem hífen)
  i18nMessages?: AccordionI18nContext;
  multiOpen?: boolean;
  idBase?: string;
  disableTransitions?: boolean;         // NEW
  unmountOnExit?: boolean;              // NEW
} & React.HTMLAttributes<HTMLDivElement>;

const AccordionRoot = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      children,
      defaultOpenIndex = null,
      openIndex: controlledIndex,
      onChange,
      theme = "light",
      className,
      "data-testid": dataTestIdAttr,
      dataTestId,
      i18nMessages,
      multiOpen = false,
      idBase = "accordion",
      disableTransitions = false,       // NEW
      unmountOnExit = false,            // NEW
      ...divProps
    },
    ref
  ) => {
    const [internalIndex, setInternalIndex] = useState<number | number[] | null>(
      multiOpen
        ? Array.isArray(defaultOpenIndex)
          ? defaultOpenIndex
          : typeof defaultOpenIndex === "number"
          ? [defaultOpenIndex]
          : []
        : typeof defaultOpenIndex === "number"
        ? defaultOpenIndex
        : null
    );
    const isControlled = controlledIndex !== undefined;
    const openIndex = isControlled
      ? controlledIndex
      : internalIndex === null
        ? (multiOpen ? [] : -1)
        : internalIndex;

    const setOpenIndex = (index: number | number[]) => {
      if (multiOpen && (index === null || index === undefined)) {
        if (!isControlled) setInternalIndex([]);
        onChange?.([]);
        return;
      }
      if (!isControlled) setInternalIndex(index);
      onChange?.(index);
    };

    const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const contextI18n = useContext(AccordionMessagesContext);
    const i18n = i18nMessages || contextI18n;

    const registerHeader = React.useCallback(
      (ref: HTMLDivElement | null, idx: number) => {
        headerRefs.current[idx] = ref;
      },
      []
    );

    const finalDataTestId = dataTestIdAttr ?? dataTestId ?? "accordion"; // NEW
    return (
      <AccordionMessagesContext.Provider value={i18n}>
        <AccordionContext.Provider value={{
          openIndex, setOpenIndex, theme, registerHeader, headerRefs, multiOpen,
          disableTransitions, unmountOnExit                        // NEW
        }}>
          <div
            ref={ref}
            className={clsx(
              "w-full rounded-lg overflow-hidden transition-colors duration-200",
              theme === "dark"
                ? "bg-[#23272f] shadow-lg"
                : "bg-[#f8f8f8] shadow-md",
              className
            )}
            data-testid={finalDataTestId}  // UPDATED
            {...divProps}
          >
            {React.Children.map(children, child =>
              React.isValidElement(child)
                ? React.cloneElement(child as React.ReactElement<Record<string, unknown>>, { idBase })
                : child
            )}
          </div>
        </AccordionContext.Provider>
      </AccordionMessagesContext.Provider>
    );
  }
);
AccordionRoot.displayName = "AccordionRoot";

type ItemProps = {
  children: ReactNode;
  index: number;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
  dataTestId?: string;                 // NEW
};

const Item: React.FC<ItemProps & { idBase?: string }> = React.memo(
  ({ children, index, disabled = false, className, "data-testid": dataTestIdAttr, dataTestId, idBase }) => {
    const ctx = useContext(AccordionContext);
    const theme = ctx?.theme ?? "light";
    const finalDataTestId = dataTestIdAttr ?? dataTestId ?? `accordion-item-${index}`; // NEW
    return (
      <div
        className={clsx(
          "accordion-item",
          "border-t first:border-t-0",
          theme === "dark"
            ? "border-[#35373e]"
            : "border-[#e0e0e0]",
          className
        )}
        data-testid={finalDataTestId}   // UPDATED
      >
        {React.Children.map(children, child =>
          React.isValidElement(child)
            ? React.cloneElement(
                child as ReactElement<{ index?: number; disabled?: boolean; idBase?: string }>,
                {
                  index,
                  // Preserve child's own disabled if set; parent disabled has priority
                  disabled: disabled || (child.props as Record<string, unknown>)?.disabled === true, // CHANGED
                  idBase
                }
              )
            : child
        )}
      </div>
    );
  }
);
Item.displayName = "AccordionItem";

type HeaderProps = {
  children: ReactNode;
  index: number;
  label?: string;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
  dataTestId?: string;                 // NEW
  "aria-label"?: string;
};

const Header: React.FC<HeaderProps & { idBase?: string }> = React.memo(
  ({
    children,
    index,
    label,
    disabled = false,
    className,
    "data-testid": dataTestIdAttr,
    dataTestId,
    "aria-label": ariaLabel,
    idBase = "accordion"
  }) => {
    const ctx = useContext(AccordionContext);
    const i18n = useContext(AccordionMessagesContext);
    if (!ctx) throw new Error("Accordion.Header must be used within Accordion");

    const { openIndex, setOpenIndex, theme, registerHeader, headerRefs, multiOpen } = ctx;
    // PATCH: Se disabled, nunca fica aberto
    const isOpen = disabled
      ? false
      : multiOpen
        ? Array.isArray(openIndex) && openIndex.includes(index)
        : openIndex === index;

    const headerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      registerHeader(headerRef.current, index);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, registerHeader]);

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (!headerRefs.current.length) return;
      if (e.key === "ArrowDown") {
        focusNextEnabledHeader(headerRefs.current, index, "next");
        e.preventDefault();
      } else if (e.key === "ArrowUp") {
        focusNextEnabledHeader(headerRefs.current, index, "prev");
        e.preventDefault();
      } else if (e.key === "Home") {
        focusNextEnabledHeader(headerRefs.current, index, "first");
        e.preventDefault();
      } else if (e.key === "End") {
        focusNextEnabledHeader(headerRefs.current, index, "last");
        e.preventDefault();
      } else if ((e.key === "Enter" || e.key === " ") && !disabled) {
        if (multiOpen) {
          const arr = Array.isArray(openIndex) ? openIndex : [];
          const newArr = isOpen
            ? arr.filter((i) => i !== index)
            : [...arr, index];
          setOpenIndex(newArr);
        } else {
          setOpenIndex(isOpen ? (multiOpen ? [] : -1) : index);
        }
        e.preventDefault();
      }
    };

    const finalDataTestId = dataTestIdAttr ?? dataTestId ?? `accordion-header-${index}`; // NEW
    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={disabled ? "false" : isOpen ? "true" : "false"}
        aria-controls={`${idBase}-panel-${index}`}
        id={`${idBase}-header-${index}`}
        aria-label={ariaLabel ?? i18n.getHeaderLabel?.(index, label)}
        aria-disabled={disabled ? "true" : undefined}
        data-disabled={disabled ? "true" : "false"}
        data-state={isOpen ? "open" : "closed"}
        data-testid={finalDataTestId}            // ADDED
        ref={headerRef}
        style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%" }}
        className={clsx(
          "flex items-center justify-between w-full h-full px-4 py-4 sm:px-3 sm:py-3 font-semibold font-inter transition-colors duration-200 select-none outline-none",
          theme === "dark"
            ? "bg-[#23272f] text-[#f3f2f1] hover:bg-[#282c36] focus:bg-[#282c36]"
            : "bg-white text-[#222] hover:bg-[#f0f0f0] focus:bg-[#f0f0f0]",
          isOpen &&
            (theme === "dark"
              ? "bg-[#1b1e27] text-[#7b5cff]"
              : "bg-[#f5f8ff] text-[#2564cf]"),
          disabled &&
            (theme === "dark"
              ? "text-[#666] bg-[#23272f] cursor-not-allowed opacity-70 pointer-events-none"
              : "text-[#595959] bg-[#fafafa] cursor-not-allowed opacity-70 pointer-events-none"),
          "focus-visible:outline-2 focus-visible:outline-offset-2",
          theme === "dark"
            ? "focus-visible:outline-[#7b5cff]"
            : "focus-visible:outline-[#2564cf]",
          className
        )}
        onClick={() => {
          if (!disabled) {
            if (multiOpen) {
              const arr = Array.isArray(openIndex) ? openIndex : [];
              const newArr = isOpen
                ? arr.filter((i) => i !== index)
                : [...arr, index];
              setOpenIndex(newArr);
            } else {
              setOpenIndex(isOpen ? -1 : index);
            }
          }
        }}
        onKeyDown={handleKeyDown}
      >
        <span className="text-left flex-1">{children}</span>
        <span
          className={clsx(
            "ml-2 text-inherit text-lg flex items-center w-5 h-5 justify-center items-center"
          )}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
          }}
          aria-hidden="true"
        >
          <FaChevronDown />
        </span>
      </div>
    );
  }
);
Header.displayName = "AccordionHeader";

type BodyProps = {
  children: ReactNode;
  index: number;
  className?: string;
  "data-testid"?: string;
  dataTestId?: string;                 // NEW
};

const Body: React.FC<BodyProps & { idBase?: string }> = React.memo(
  ({ children, index, className, "data-testid": dataTestIdAttr, dataTestId, idBase = "accordion" }) => {
    const ctx = useContext(AccordionContext);
    if (!ctx) throw new Error("Accordion.Body must be used within Accordion");
    const { openIndex, theme, multiOpen, disableTransitions, unmountOnExit } = ctx; // UPDATED
    const isOpen = multiOpen
      ? Array.isArray(openIndex) && openIndex.includes(index)
      : openIndex === index;

    // Se optar por desmontar e estiver fechado
    const contentRef = useRef<HTMLDivElement>(null);

    const [maxHeight, setMaxHeight] = useState<string>(isOpen ? "none" : "0px");

    useEffect(() => {
      if (disableTransitions) {
        // NEW: sem transição, apenas "none" ou "0px"
        setMaxHeight(isOpen ? "none" : "0px");
        return;
      }
      if (!isOpen) {
        setMaxHeight("0px");
        return;
      }
      if (!contentRef.current) return;
      setMaxHeight(contentRef.current.scrollHeight + "px");
      const timeout = setTimeout(() => {
        if (isOpen) setMaxHeight("none");
      }, 250);
      const ro = new window.ResizeObserver(() => {
        if (isOpen && contentRef.current) {
          setMaxHeight(contentRef.current.scrollHeight + "px");
        }
      });
      ro.observe(contentRef.current);
      return () => {
        clearTimeout(timeout);
        ro.disconnect();
      };
    }, [isOpen, disableTransitions]); // UPDATED deps

    if (unmountOnExit && !isOpen) return null; // NEW

    const finalDataTestId = dataTestIdAttr ?? dataTestId ?? `accordion-body-${index}`; // NEW
    return (
      <div
        className={clsx(
          "transition-colors duration-200 font-normal font-grotesk",
          theme === "dark"
            ? "bg-[#23272f] text-[#f3f2f1]"
            : "bg-[#fafafa] text-[#222]",
          className
        )}
        id={`${idBase}-panel-${index}`}
        role="region"
        aria-labelledby={`${idBase}-header-${index}`}
        aria-hidden={isOpen ? "false" : "true"}        // NEW
        data-state={isOpen ? "open" : "closed"}        // NEW
        data-testid={finalDataTestId}   // UPDATED
        style={{
          overflow: "hidden",
          transition: disableTransitions
            ? "none"
            : "max-height 0.25s cubic-bezier(0.4,0,0.2,1)",        // UPDATED
          maxHeight: disableTransitions ? (isOpen ? "none" : "0px") : maxHeight, // UPDATED
        }}
      >
        <div
          ref={contentRef}
          className="px-4 py-4 sm:px-3 sm:py-3"
        >
          {children}
        </div>
      </div>
    );
  }
);
Body.displayName = "AccordionBody";

type AccordionComponent = React.FC<AccordionProps> & {
  Item: typeof Item;
  Header: typeof Header;
  Body: typeof Body;
};

const Accordion = AccordionRoot as unknown as AccordionComponent;
Accordion.Item = Item;
Accordion.Header = Header;
Accordion.Body = Body;

export default Accordion;

function focusNextEnabledHeader(
  refs: (HTMLDivElement | null)[],
  currentIdx: number,
  direction: "next" | "prev" | "first" | "last"
) {
  const total = refs.length;
  if (!total) return;
  let nextIdx = currentIdx;

  if (direction === "next") {
    let tries = 0;
    do {
      nextIdx = (nextIdx + 1) % total;
      tries++;
    } while (refs[nextIdx]?.getAttribute("aria-disabled") === "true" && tries < total);
  } else if (direction === "prev") {
    let tries = 0;
    do {
      nextIdx = (nextIdx - 1 + total) % total;
      tries++;
    } while (refs[nextIdx]?.getAttribute("aria-disabled") === "true" && tries < total);
  } else if (direction === "first") {
    nextIdx = 0;
    while (refs[nextIdx]?.getAttribute("aria-disabled") === "true" && nextIdx < total) nextIdx++;
  } else if (direction === "last") {
    nextIdx = total - 1;
    while (refs[nextIdx]?.getAttribute("aria-disabled") === "true" && nextIdx > 0) nextIdx--;
  }

  if (!refs[nextIdx]?.getAttribute || refs[nextIdx]?.getAttribute("aria-disabled") === "true") return;
  refs[nextIdx]?.focus();
}